/**
 * VendorPaymentsChart Component - Phase 5 Implementation
 * Line chart for vendor payment trends
 * Shows payment patterns, amounts, and timing analysis
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Area,
  ComposedChart,
  Bar
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { 
  CreditCard, 
  TrendingUp, 
  TrendingDown, 
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../../lib/utils';

interface VendorPaymentsChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  totalPayments: {
    label: "Total Payments",
    color: "hsl(var(--chart-1))",
  },
  onTimePayments: {
    label: "On-Time Payments",
    color: "hsl(var(--chart-3))",
  },
  latePayments: {
    label: "Late Payments",
    color: "hsl(var(--chart-4))",
  },
  paymentCount: {
    label: "Payment Count",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { 
    vendor: 'TechSupply Co', 
    amount: 45000, 
    paymentDate: '2024-01-15', 
    method: 'wire',
    status: 'completed',
    daysToPayment: 28,
    onTime: true
  },
  { 
    vendor: 'Office Materials', 
    amount: 12500, 
    paymentDate: '2024-01-16', 
    method: 'check',
    status: 'completed',
    daysToPayment: 35,
    onTime: false
  },
  { 
    vendor: 'Global Logistics', 
    amount: 67000, 
    paymentDate: '2024-01-18', 
    method: 'ach',
    status: 'completed',
    daysToPayment: 25,
    onTime: true
  },
  { 
    vendor: 'Marketing Agency', 
    amount: 23000, 
    paymentDate: '2024-01-20', 
    method: 'wire',
    status: 'pending',
    daysToPayment: 30,
    onTime: true
  },
  { 
    vendor: 'Software Licenses', 
    amount: 89000, 
    paymentDate: '2024-01-22', 
    method: 'ach',
    status: 'completed',
    daysToPayment: 45,
    onTime: false
  },
  { 
    vendor: 'Utilities Corp', 
    amount: 5600, 
    paymentDate: '2024-01-25', 
    method: 'auto',
    status: 'completed',
    daysToPayment: 15,
    onTime: true
  },
  { 
    vendor: 'Equipment Rental', 
    amount: 34000, 
    paymentDate: '2024-01-28', 
    method: 'wire',
    status: 'scheduled',
    daysToPayment: 30,
    onTime: true
  },
].map((item, index) => ({
  ...item,
  date: `2024-01-${String(15 + index * 2).padStart(2, '0')}`,
  totalPayments: item.amount,
  onTimePayments: item.onTime ? item.amount : 0,
  latePayments: !item.onTime ? item.amount : 0,
  paymentCount: 1,
  cumulativeAmount: 0, // Will be calculated
}));

// Calculate cumulative amounts
const processedData = generateMockData().map((item, index, array) => ({
  ...item,
  cumulativeAmount: array.slice(0, index + 1).reduce((sum, curr) => sum + curr.amount, 0)
}));

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'text-green-600';
    case 'pending':
      return 'text-yellow-600';
    case 'scheduled':
      return 'text-blue-600';
    case 'failed':
      return 'text-red-600';
    default:
      return 'text-muted-foreground';
  }
};

const getMethodIcon = (method: string) => {
  switch (method) {
    case 'wire':
      return '🏦';
    case 'ach':
      return '🔄';
    case 'check':
      return '📝';
    case 'auto':
      return '⚡';
    default:
      return '💳';
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[260px]">
        <p className="font-medium mb-2">{data?.vendor}</p>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Payment Date:</span>
            <span className="font-medium">{formatDate(data?.paymentDate)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Amount:</span>
            <span className="font-medium">{formatCurrency(data?.amount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Method:</span>
            <span className="font-medium flex items-center space-x-1">
              <span>{getMethodIcon(data?.method)}</span>
              <span className="capitalize">{data?.method}</span>
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Status:</span>
            <span className={cn("font-medium capitalize", getStatusColor(data?.status))}>
              {data?.status}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Days to Payment:</span>
            <span className={cn(
              "font-medium",
              data?.onTime ? "text-green-600" : "text-orange-600"
            )}>
              {data?.daysToPayment} days {data?.onTime ? '✓' : '⚠️'}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const VendorPaymentsChart: React.FC<VendorPaymentsChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : processedData;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalPayments = chartData.reduce((sum, item) => sum + item.amount, 0);
  const onTimePayments = chartData.filter(item => item.onTime).length;
  const onTimePercentage = (onTimePayments / chartData.length) * 100;
  const averageDays = chartData.reduce((sum, item) => sum + item.daysToPayment, 0) / chartData.length;
  const pendingPayments = chartData.filter(item => item.status === 'pending' || item.status === 'scheduled').length;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">Total Payments</p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {formatCurrency(totalPayments)}
              </p>
            </div>
            <div className="text-blue-600">
              <CreditCard className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">On-Time Rate</p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                {onTimePercentage.toFixed(1)}%
              </p>
            </div>
            <div className="text-green-600">
              <CheckCircle className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">Avg Payment Days</p>
              <p className="text-xl font-bold text-yellow-800 dark:text-yellow-200">
                {averageDays.toFixed(0)} days
              </p>
            </div>
            <div className="text-yellow-600">
              <Clock className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "rounded-lg p-4 border",
          pendingPayments > 0 
            ? "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
            : "bg-muted/30"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                "text-sm",
                pendingPayments > 0 
                  ? "text-orange-700 dark:text-orange-300"
                  : "text-muted-foreground"
              )}>
                Pending Payments
              </p>
              <p className={cn(
                "text-xl font-bold",
                pendingPayments > 0 
                  ? "text-orange-800 dark:text-orange-200"
                  : "text-muted-foreground"
              )}>
                {pendingPayments}
              </p>
            </div>
            <div className={cn(
              pendingPayments > 0 ? "text-orange-600" : "text-muted-foreground"
            )}>
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Trends Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="amount"
              orientation="left"
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="count"
              orientation="right"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <Bar
              yAxisId="amount"
              dataKey="onTimePayments"
              fill="var(--color-onTimePayments)"
              name="On-Time Payments"
              radius={[2, 2, 0, 0]}
            />
            <Bar
              yAxisId="amount"
              dataKey="latePayments"
              fill="var(--color-latePayments)"
              name="Late Payments"
              radius={[2, 2, 0, 0]}
            />
            <Line
              yAxisId="amount"
              type="monotone"
              dataKey="cumulativeAmount"
              stroke="var(--color-totalPayments)"
              strokeWidth={3}
              dot={{ fill: "var(--color-totalPayments)", strokeWidth: 2, r: 4 }}
              name="Cumulative Amount"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};